import { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import * as Icons from "lucide-react";
import { submitLeadForm } from "../mock";
import { toast } from "sonner";

export const RequestQuote = () => {
  const [formData, setFormData] = useState({
    companyName: "",
    contactPerson: "",
    email: "",
    phone: "",
    deviceCount: "",
    manufacturer: "",
    models: "",
    condition: "",
    location: "",
    pickupTimeframe: "",
    additionalInfo: "",
  });
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Create FormData object
      const formDataObj = new FormData();
      
      // Append all form fields
      formDataObj.append('companyName', formData.companyName);
      formDataObj.append('contactPerson', formData.contactPerson);
      formDataObj.append('email', formData.email);
      formDataObj.append('phone', formData.phone);
      formDataObj.append('deviceCount', formData.deviceCount);
      formDataObj.append('manufacturer', formData.manufacturer);
      formDataObj.append('models', formData.models || '');
      formDataObj.append('condition', formData.condition);
      formDataObj.append('location', formData.location);
      formDataObj.append('pickupTimeframe', formData.pickupTimeframe);
      formDataObj.append('additionalInfo', formData.additionalInfo || '');
      
      // Append file if exists
      if (file) {
        formDataObj.append('file', file);
      }

      // Send to backend API
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/leads`, {
        method: 'POST',
        body: formDataObj,
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast.success(result.message || "Ihre Anfrage wurde erfolgreich übermittelt");
        
        // Reset form
        setFormData({
          companyName: "",
          contactPerson: "",
          email: "",
          phone: "",
          deviceCount: "",
          manufacturer: "",
          models: "",
          condition: "",
          location: "",
          pickupTimeframe: "",
          additionalInfo: "",
        });
        setFile(null);
      } else {
        toast.error(result.detail || result.message || "Ein Fehler ist aufgetreten");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error("Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200 border-0 mb-4">
              Nur für Firmenkunden
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Firmen-Angebot anfragen
            </h1>
            <p className="text-xl text-gray-600">
              Füllen Sie das Formular aus und erhalten Sie Ihr individuelles Ankaufsangebot
            </p>
          </div>

          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <Card className="border-blue-100">
              <CardContent className="p-6 text-center">
                <Icons.Clock className="text-blue-600 mx-auto mb-2" size={32} />
                <h3 className="font-semibold text-gray-900 mb-1">Schnelle Antwort</h3>
                <p className="text-sm text-gray-600">Angebot in 24-48h</p>
              </CardContent>
            </Card>
            <Card className="border-blue-100">
              <CardContent className="p-6 text-center">
                <Icons.ShieldCheck className="text-blue-600 mx-auto mb-2" size={32} />
                <p className="font-semibold text-gray-900 mb-1">100% Sicher</p>
                <p className="text-sm text-gray-600">DSGVO-konform</p>
              </CardContent>
            </Card>
            <Card className="border-blue-100">
              <CardContent className="p-6 text-center">
                <Icons.CheckCircle2 className="text-blue-600 mx-auto mb-2" size={32} />
                <p className="font-semibold text-gray-900 mb-1">Unverbindlich</p>
                <p className="text-sm text-gray-600">Kostenlose Bewertung</p>
              </CardContent>
            </Card>
          </div>

          {/* Form */}
          <Card className="border-blue-200 shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl">Ihre Anfrage</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Company Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Icons.Building2 className="mr-2 text-blue-600" size={20} />
                    Unternehmensdaten
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="companyName">Firmenname *</Label>
                      <Input
                        id="companyName"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        placeholder="Ihr Firmenname"
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contactPerson">Ansprechpartner *</Label>
                      <Input
                        id="contactPerson"
                        name="contactPerson"
                        value={formData.contactPerson}
                        onChange={handleInputChange}
                        placeholder="Vor- und Nachname"
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Geschäftliche E-Mail *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="email@firma.de"
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Telefon *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+49 (0) 123 456789"
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Device Information */}
                <div className="space-y-4 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Icons.Laptop className="mr-2 text-blue-600" size={20} />
                    Geräteinformationen
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="deviceCount">Anzahl Geräte *</Label>
                      <Input
                        id="deviceCount"
                        name="deviceCount"
                        type="number"
                        min="20"
                        value={formData.deviceCount}
                        onChange={handleInputChange}
                        placeholder="Mindestens 20"
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="manufacturer">Hersteller *</Label>
                      <Select
                        value={formData.manufacturer}
                        onValueChange={(value) => handleSelectChange("manufacturer", value)}
                        required
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Wählen Sie einen Hersteller" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dell">Dell</SelectItem>
                          <SelectItem value="lenovo">Lenovo</SelectItem>
                          <SelectItem value="hp">HP</SelectItem>
                          <SelectItem value="apple">Apple</SelectItem>
                          <SelectItem value="fujitsu">Fujitsu</SelectItem>
                          <SelectItem value="mixed">Gemischt</SelectItem>
                          <SelectItem value="other">Sonstige</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="models">Modelle</Label>
                    <Input
                      id="models"
                      name="models"
                      value={formData.models}
                      onChange={handleInputChange}
                      placeholder="z.B. Dell Latitude 5420, Lenovo ThinkPad T14"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="condition">Gerätezustand *</Label>
                    <Select
                      value={formData.condition}
                      onValueChange={(value) => handleSelectChange("condition", value)}
                      required
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Wählen Sie den Zustand" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="excellent">Sehr gut (wie neu)</SelectItem>
                        <SelectItem value="good">Gut (leichte Gebrauchsspuren)</SelectItem>
                        <SelectItem value="fair">Befriedigend (sichtbare Gebrauchsspuren)</SelectItem>
                        <SelectItem value="poor">Ausreichend (starke Gebrauchsspuren)</SelectItem>
                        <SelectItem value="mixed">Gemischt</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Logistics */}
                <div className="space-y-4 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Icons.Truck className="mr-2 text-blue-600" size={20} />
                    Logistik
                  </h3>
                  <div>
                    <Label htmlFor="location">Standort / PLZ *</Label>
                    <Input
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="Stadt oder PLZ"
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pickupTimeframe">Gewünschter Abholzeitraum *</Label>
                    <Select
                      value={formData.pickupTimeframe}
                      onValueChange={(value) => handleSelectChange("pickupTimeframe", value)}
                      required
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Wählen Sie einen Zeitraum" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asap">So schnell wie möglich</SelectItem>
                        <SelectItem value="1-2weeks">In 1-2 Wochen</SelectItem>
                        <SelectItem value="2-4weeks">In 2-4 Wochen</SelectItem>
                        <SelectItem value="1-3months">In 1-3 Monaten</SelectItem>
                        <SelectItem value="flexible">Flexibel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="space-y-4 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Icons.FileText className="mr-2 text-blue-600" size={20} />
                    Zusätzliche Informationen
                  </h3>
                  <div>
                    <Label htmlFor="additionalInfo">Weitere Informationen</Label>
                    <Textarea
                      id="additionalInfo"
                      name="additionalInfo"
                      value={formData.additionalInfo}
                      onChange={handleInputChange}
                      placeholder="Besondere Anforderungen, Fragen oder weitere Details..."
                      rows={4}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="file">Geräteliste hochladen (Excel oder PDF)</Label>
                    <div className="mt-2">
                      <label
                        htmlFor="file"
                        className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        <div className="text-center">
                          <Icons.Upload className="mx-auto text-blue-600 mb-2" size={32} />
                          <p className="text-sm text-gray-600">
                            {file ? file.name : "Klicken Sie hier, um eine Datei hochzuladen"}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">Excel oder PDF, max. 10MB</p>
                        </div>
                        <input
                          id="file"
                          type="file"
                          accept=".xlsx,.xls,.pdf"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Notice */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <div className="flex items-start space-x-3">
                    <Icons.Info className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                    <div className="text-sm text-gray-700">
                      <p className="font-semibold mb-2">Hinweis:</p>
                      <p>
                        Dieser Service ist ausschließlich für Firmenkunden. Privatanfragen werden nicht bearbeitet. 
                        Mindestmenge: 20 Geräte. Alle Angaben werden vertraulich behandelt und DSGVO-konform verarbeitet.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium"
                  >
                    {isSubmitting ? (
                      <>
                        <Icons.Loader2 className="mr-2 animate-spin" size={20} />
                        Wird gesendet...
                      </>
                    ) : (
                      <>
                        Angebot anfordern
                        <Icons.Send className="ml-2" size={20} />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">
              Fragen? Kontaktieren Sie uns direkt:
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
              <a
                href="mailto:ankauf@wirkaufendeinlaptop.de"
                className="flex items-center text-[#1f7db6] hover:text-[#143e6e]"
              >
                <Icons.Mail className="mr-2" size={18} />
                ankauf@wirkaufendeinlaptop.de
              </a>
              <a
                href="tel:+4945193105207"
                className="flex items-center text-[#1f7db6] hover:text-[#143e6e]"
              >
                <Icons.Phone className="mr-2" size={18} />
                +49 4519 3105207
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
